import { motion } from 'framer-motion'
import type { CheckResult } from '../lib/checkSubscription'
import { trackSubscriptionBlockAction } from '../lib/analytics'

interface AlreadySubscribedProps {
  email: string
  platform: CheckResult['platform']
  onUseDifferentEmail: () => void
}

const platformLabel: Record<NonNullable<CheckResult['platform']>, string> = {
  ios: 'on the App Store',
  android: 'on Google Play',
  web: 'through our website',
  other: '',
}

export function AlreadySubscribed({ email, platform, onUseDifferentEmail }: AlreadySubscribedProps) {
  const where = platform ? platformLabel[platform] : ''

  return (
    <div className="min-h-dvh flex flex-col bg-white">
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md text-center"
        >
          <div className="text-6xl mb-6">👋</div>

          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
            Welcome back!
          </h1>

          <p className="text-gray-600 mb-2">
            Looks like <span className="font-semibold text-gray-900">{email}</span> already
            has an active KidneyPal Pro subscription{where ? ` ${where}` : ''}.
          </p>
          <p className="text-gray-500 mb-8 text-sm">
            Open the KidneyPal app and sign in with this email to access your plan.
          </p>

          <div className="space-y-3 mb-6">
            <a
              href="https://apps.apple.com/us/app/kidney-pal-renal-diet-tracker/id1523371555"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackSubscriptionBlockAction('app_store')}
              className="flex items-center justify-center gap-3 w-full py-4 px-6 bg-black text-white rounded-xl hover:bg-gray-800 transition-colors"
            >
              <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
              </svg>
              <div className="text-left">
                <div className="text-xs">Download on the</div>
                <div className="text-base font-semibold">App Store</div>
              </div>
            </a>

            <a
              href="https://play.google.com/store/apps/details?id=com.prestigeworldwide.kidneyrecipes"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackSubscriptionBlockAction('play_store')}
              className="flex items-center justify-center gap-3 w-full py-4 px-6 bg-black text-white rounded-xl hover:bg-gray-800 transition-colors"
            >
              <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3.18 23.04c-.46-.46-.46-1.22-.46-2.75V3.71c0-1.53 0-2.29.46-2.75L13.02 12 3.18 23.04zm1.35.79L16.38 13.2l-2.55-2.55L4.53 23.83zm14.32-10.4l3.28-1.77c.62-.34.62-.98 0-1.32l-3.28-1.77-3.06 2.43 3.06 2.43zM4.53.17l9.3 13.18 2.55-2.55L4.53.17z" />
              </svg>
              <div className="text-left">
                <div className="text-xs">Get it on</div>
                <div className="text-base font-semibold">Google Play</div>
              </div>
            </a>
          </div>

          <button
            onClick={() => {
              trackSubscriptionBlockAction('use_different_email')
              onUseDifferentEmail()
            }}
            className="text-sm text-gray-500 underline hover:text-gray-700"
          >
            Use a different email
          </button>

          <p className="text-xs text-gray-400 mt-8">
            Need help?{' '}
            <a href="mailto:support@kidneypal.app" className="underline hover:text-gray-600">
              support@kidneypal.app
            </a>
          </p>
        </motion.div>
      </div>
    </div>
  )
}
